// SEASPrivacyPolicy.js
// React component rendering the SEAS Privacy Policy

import React from "react";
import DocumentMeta from "./DocumentMeta";
import { ROUTE_META } from "./routeMeta";

export default function SEASPrivacyPolicy() {
  return (
    <main className="max-w-4xl mx-auto px-6 py-8 bg-white">
      <DocumentMeta
        title={ROUTE_META.seas.title}
        description={ROUTE_META.seas.description}
        robots={ROUTE_META.seas.robots}
      />
      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1>SEAS Privacy Policy</h1>
          <p>
            <strong>Effective date:</strong> November 4, 2025
            <br />
            <strong>Last updated:</strong> November 4, 2025
            <br />
            <strong>App:</strong> SEAS (the "App")
            <br />
            <strong>Operator:</strong> Appazzola LLC, 231 E Canal St, Hershey, PA 17033 ("Appazzola", "we", "us", "our")
            <br />
            <strong>General contact:</strong> contact@appazzola.com
            <br />
            <strong>Privacy and legal contact:</strong> legal@appazzola.com
          </p>
        </header>

        <section className="mb-8">
          <h2>At a glance</h2>
          <ul>
            <li>Operated by Appazzola LLC.</li>
            <li>We collect only the identifiers needed to deliver alerts and support diagnostics.</li>
            <li>No personal information such as names, email, contacts, photos, or precise location is collected.</li>
            <li>Data is processed in the United States; you may request deletion at any time.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Scope</h2>
          <p>
            This policy covers the SEAS mobile application, supporting notification services, and any related infrastructure used to deliver emergency alerts.
          </p>
        </section>

        <section className="mb-8">
          <h2>Data we collect</h2>
          <ul>
            <li>
              <strong>Device identifiers</strong>
              <ul>
                <li>Expo push token used to deliver notifications to your device.</li>
                <li>App-generated device ID (pseudonymous) used to correlate diagnostics and delivery attempts.</li>
              </ul>
            </li>
            <li>
              <strong>App preferences (stored locally on your device)</strong>
              <ul>
                <li>Notification toggles, refresh interval, county and municipality filters, incident-type settings, theme choice.</li>
              </ul>
            </li>
            <li>
              <strong>Server-synced notification profile (per device)</strong>
              <ul>
                <li>County enablement, global vs municipality mode, municipality selections, incident-type toggles.</li>
              </ul>
            </li>
            <li>
              <strong>Diagnostics and technical logs</strong>
              <ul>
                <li>Device/app metadata (platform, OS version, app version, network state), token registration status, timestamps, aggregate delivery metrics.</li>
              </ul>
            </li>
            <li>
              <strong>Not collected</strong>
              <ul>
                <li>No names, email addresses, precise GPS data, contacts, photos/media, payment data, health information, SMS, or calendars.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>How we use information</h2>
          <ul>
            <li>Deliver push notifications you have enabled.</li>
            <li>Prevent duplicate notifications using short-lived deduplication keys.</li>
            <li>Route alerts according to the profile you configure (county, municipality, incident type).</li>
            <li>Monitor service health and reliability through aggregated diagnostics.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Where data is stored</h2>
          <ul>
            <li>
              <strong>On device</strong> – App preferences, saved incidents, and notes remain on your device. Removing the app deletes this data.
            </li>
            <li>
              <strong>Server-side (United States)</strong> – Push tokens, notification profiles, diagnostics, dedupe keys, and delivery metrics are stored in Google Firestore and related Google Cloud services.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Retention</h2>
          <ul>
            <li>Push tokens and profiles are retained while valid; inactive or invalid tokens are purged (typically within about 60 days).</li>
            <li>Deduplication keys expire within roughly 24 hours and exist solely to prevent repeat alerts.</li>
            <li>Diagnostics are retained as needed for reliability and are periodically pruned.</li>
            <li>Aggregate delivery metrics are anonymized and retained for operational monitoring.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Legal basis (where applicable)</h2>
          <ul>
            <li><strong>Legitimate interests</strong> – ensuring alerts reach subscribed users and preventing duplicate or erroneous notifications.</li>
            <li><strong>Consent</strong> – enabling push notifications; you can withdraw consent by disabling notifications in your device settings.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Your choices and rights</h2>
          <ul>
            <li>Disable notifications through your device OS settings at any time.</li>
            <li>Adjust filters, refresh intervals, and other preferences directly in the app.</li>
            <li>Request removal of your push token, associated profile, and diagnostics by emailing <a href="mailto:contact@appazzola.com">contact@appazzola.com</a>. Include your device platform and, if available, your app-generated device ID (Settings → Diagnostics).</li>
            <li>Uninstalling the app invalidates the push token during the next cleanup cycle.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Sharing and third parties</h2>
          <ul>
            <li>Apple Push Notification service (APNs) for iOS delivery.</li>
            <li>Google Firebase, Cloud Functions, and Firestore for hosting, scheduling, and storage.</li>
            <li>Expo Push Service for cross-platform token handling and routing.</li>
            <li>We do not sell or rent your data. Disclosure happens only to provide the service, comply with law, or protect our rights.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>Security</h2>
          <ul>
            <li>TLS encryption protects data in transit.</li>
            <li>Firestore security rules and least-privilege service accounts restrict access.</li>
            <li>Operational controls include token pruning and monitoring for delivery anomalies.</li>
            <li>While no method is perfect, we apply reasonable safeguards aligned with industry best practices.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2>International transfers</h2>
          <p>
            Data is primarily processed in the United States. By using the app from outside the U.S., you consent to transferring and processing your information in the United States.
          </p>
        </section>

        <section className="mb-8">
          <h2>Children&apos;s privacy</h2>
          <p>SEAS is not directed to children under 13, and we do not knowingly collect personal information from them. If we become aware of such data, we will delete it promptly.</p>
        </section>

        <section className="mb-8">
          <h2>Policy changes</h2>
          <p>We may update this policy. Material changes will be highlighted in-app and by updating the dates at the top of this page.</p>
        </section>

        <section className="mb-8">
          <h2>Contact</h2>
          <p>
            Appazzola LLC<br />
            Email: <a href="mailto:contact@appazzola.com">contact@appazzola.com</a>
          </p>
        </section>

        <section className="mb-8">
          <h2>Google Play Data Safety summary</h2>
          <ul>
            <li>
              <strong>Collected</strong>
              <ul>
                <li>Device or other IDs: Expo push token, app-generated device ID (diagnostics and delivery reliability).</li>
                <li>App info and performance: device model, OS/app version, network state, token registration status.</li>
              </ul>
            </li>
            <li>
              <strong>Not collected</strong>
              <ul>
                <li>Personal info (name, email), location, contacts, health/fitness, financial, photos/media, files, calendar, SMS/phone data.</li>
              </ul>
            </li>
            <li>Data is not sold; it is used only for alert delivery, deduplication, and reliability monitoring.</li>
            <li>Data is encrypted in transit. Request deletion via <a href="mailto:contact@appazzola.com">contact@appazzola.com</a> or uninstall the app to invalidate the push token automatically.</li>
          </ul>
        </section>
      </article>
    </main>
  );
}
